<script setup>
import { computed, ref } from 'vue';

import { usePlatform } from '../composables/usePlatform';
import { HrMenu } from '../primitives';
import HrBottomSheet from '../primitives/HrBottomSheet.vue';

/**
 * Context-aware menu — renders as a floating HrMenu on desktop and a bottom sheet on mobile.
 *
 * Fully controlled: open and close are driven solely by `v-model`. The trigger must flip the
 * model (e.g. `@click="open = true"`); the desktop menu's parent-click auto-toggle is disabled so
 * both surfaces behave identically.
 *
 * @status recommended
 * @group actions
 * @useWhen You need a menu that adapts automatically between desktop and mobile interaction patterns
 * @avoidWhen You need desktop-only or mobile-only behavior — use HrMenu or HrBottomSheet directly
 * @related HrActionMenu
 * @within hr-btn
 * @a11y Delegates to HrMenu (desktop) or HrBottomSheet (mobile) for focus trapping and keyboard nav.
 * @event show Fired when the menu or bottom sheet opens.
 * @event hide Fired when the menu or bottom sheet closes.
 */
defineOptions({ name: 'HrAdaptiveMenu' });

defineProps({
  /** Controls open/closed state via v-model. */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /** Title shown in the bottom-sheet header on mobile. Not rendered on desktop. */
  title: {
    type: String,
    default: null,
  },
  /**
   * Anchor corner of the menu relative to its target. Desktop only — ignored on mobile.
   * @values top left, top middle, top right, top start, top end, center left, center middle, center right,
   * center start, center end, bottom left, bottom middle, bottom right, bottom start, bottom end
   */
  anchor: {
    type: String,
    default: null,
  },
  /**
   * Menu's own alignment corner that attaches to the anchor. Desktop only — ignored on mobile.
   * @values top left, top middle, top right, top start, top end, center left, center middle, center right,
   * center start, center end, bottom left, bottom middle, bottom right, bottom start, bottom end
   */
  self: {
    type: String,
    default: null,
  },
  /**
   * Close the menu automatically when the user clicks anywhere inside it. Desktop only — ignored on mobile.
   */
  autoClose: {
    type: Boolean,
    default: false,
  },
  /**
   * What the slot holds, which is what decides whether the panel insets it.
   *
   * `items` — menu rows, which carry their own inset and whose state layer should reach every
   * panel edge, as it does in the action and overflow menus. `freeform` — anything else, such as
   * a form or a picker, which the panel insets because the content does not do it itself.
   *
   * Only the caller knows which it is, so the panel cannot infer it. Desktop only for now: the
   * mobile bottom sheet applies its own content padding to either kind.
   * @values items, freeform
   */
  content: {
    type: String,
    default: 'items',
    validator: (v) => ['items', 'freeform'].includes(v),
  },
});

const emit = defineEmits({
  /** Fired when the menu or bottom sheet open state changes. */
  'update:modelValue': null,
});

const isMobile = computed(() => usePlatform().is.mobile);

const menuRef = ref(null);

defineExpose({
  /** Programmatically open the menu or bottom sheet. */
  show: () => menuRef.value?.show(),
  /** Programmatically close the menu or bottom sheet. */
  hide: () => menuRef.value?.hide(),
  /**
   * Re-measures the desktop panel and places it again, for contents that change size while it is
   * open. A no-op on mobile, where the bottom sheet is not positioned against a trigger.
   */
  updatePosition: () => menuRef.value?.updatePosition?.(),
});
</script>

<template>
  <hr-menu
    v-if="!isMobile"
    ref="menuRef"
    v-bind="$attrs"
    no-parent-event
    :model-value="modelValue"
    :anchor="anchor"
    :self="self"
    :auto-close="autoClose"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div
      class="hr-adaptive-menu__content"
      :class="{ 'hr-adaptive-menu__content--freeform': content === 'freeform' }"
    >
      <!-- @slot Menu or bottom-sheet content. Receives `{ isMobile }` — use to adapt layout for touch. -->
      <slot :is-mobile="false" />
    </div>
  </hr-menu>
  <hr-bottom-sheet
    v-else
    ref="menuRef"
    v-bind="$attrs"
    :model-value="modelValue"
    :title="title"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template
      v-if="$slots.header"
      #header
    >
      <!-- @slot header Bottom-sheet header area — only rendered on mobile; silently ignored on desktop. -->
      <slot name="header" />
    </template>
    <slot :is-mobile="true" />
  </hr-bottom-sheet>
</template>

<style lang="scss">

/*
 * Not scoped — q-menu teleports outside the component DOM.
 *
 * Only freeform content is inset. Menu rows are left to reach every panel edge, matching the
 * action and overflow menus, which render into a bare HrMenu with no wrapper: their state layer
 * runs the full width and the panel's own overflow clips it to the corner radius.
 */
.hr-adaptive-menu__content--freeform {
  padding: var(--app-comp-adaptive-menu-content-padding);
}

</style>
